// Wires the lightweight edit affordances on the academy course
// detail page: the "Edit course" sidebar modal, the per-lesson
// "Edit" modal, and the inline module-title rename. All three
// patterns share the same modal/close/backdrop wiring as the
// add-lesson modal but stay separate from the tus upload flows.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const COURSE_MODAL = "[data-edit-course-modal]";
const LESSON_MODAL = "[data-edit-lesson-modal]";
const MODULE_TITLE_FORM = "[data-module-title-form]";

export function initAcademyEdit(root = document) {
  if (!root.querySelectorAll) return;
  root.querySelectorAll(COURSE_MODAL).forEach(wireCourseModal);
  root.querySelectorAll(LESSON_MODAL).forEach(wireLessonModal);
  root.querySelectorAll(MODULE_TITLE_FORM).forEach(wireModuleTitleForm);
}

// Delegated rather than bound per button: the failed-video card carries
// its own "Replace video" trigger and can arrive from an HTMX swap long
// after the modal is wired, in a subtree that holds no modal for the
// re-bootstrap to find. One listener for the module, which finds the
// modal at click time, outlives any swap of the modal itself.
document.addEventListener("click", (event) => {
  if (!event.target.closest("[data-edit-course-open]")) return;

  const modal = document.querySelector(COURSE_MODAL);
  if (modal) openModal(modal, "input[name=title]");
});

function wireCourseModal(modal) {
  if (!firstBind(modal)) return;

  modal
    .querySelectorAll("[data-edit-course-close]")
    .forEach((btn) => btn.addEventListener("click", () => closeModal(modal)));
  const backdrop = modal.querySelector("[data-edit-course-backdrop]");
  if (backdrop) backdrop.addEventListener("click", () => closeModal(modal));
  modal.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeModal(modal);
  });
}

function wireLessonModal(modal) {
  if (!firstBind(modal)) return;

  const lessonID = modal.dataset.lessonId;
  if (!lessonID) return;

  document
    .querySelectorAll(`[data-edit-lesson-open="${cssEscape(lessonID)}"]`)
    .forEach((btn) =>
      btn.addEventListener("click", () =>
        openModal(modal, "input[name=title]"),
      ),
    );
  modal
    .querySelectorAll("[data-edit-lesson-close]")
    .forEach((btn) => btn.addEventListener("click", () => closeModal(modal)));
  const backdrop = modal.querySelector("[data-edit-lesson-backdrop]");
  if (backdrop) backdrop.addEventListener("click", () => closeModal(modal));
  modal.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeModal(modal);
  });
}

// Inline module-title rename: reveal the Save button once the user
// changes the title text, hide it again on blur if untouched. Submit
// on Enter; reset on Escape.
function wireModuleTitleForm(form) {
  if (!firstBind(form)) return;

  const input = form.querySelector("[data-module-title-input]");
  const save = form.querySelector("[data-module-title-save]");
  if (!input || !save) return;

  const initial = input.value;

  const sync = () => {
    if (input.value.trim() !== initial.trim() && input.value.trim() !== "") {
      save.classList.remove("hidden");
    } else {
      save.classList.add("hidden");
    }
  };

  input.addEventListener("input", sync);
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      input.value = initial;
      sync();
      input.blur();
    }
  });
}

function openModal(modal, focusSelector) {
  modal.classList.remove("hidden");
  if (focusSelector) {
    const target = modal.querySelector(focusSelector);
    if (target) target.focus();
  }
}

function closeModal(modal) {
  modal.classList.add("hidden");
}

function cssEscape(value) {
  if (window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(value);
  }
  return String(value).replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
